"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useImport } from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { useToast } from "@/context/ToastContext";
import { useCreateUsageLimitRequest } from "@/utils/hooks/usage/usageLimitHooks";
import { importBlockedMessage, usageLimitMessage } from "@/utils/import/importUsage";
import { savePendingImport } from "@/utils/import/pendingImport";
import { useState } from "react";

/**
 * The rows do not fit what the plan has left. The user can ask Orquesti for more: what
 * is asked and approved is capacity for the company, and the file waits in this
 * browser (never in Orquesti) until the answer arrives in the bell.
 */
export default function ImportLimitNotice() {
  const { importData, setImportData } = useImport();
  const { usage, selectedProfile, source } = importData;
  const { session } = useSession();
  const { showToast } = useToast();
  const createRequest = useCreateUsageLimitRequest();

  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  if (!usage || usage.allowed !== false) return null;

  // Blocked: no request to make, the reason is what the user needs.
  if (usage.blocked) {
    return (
      <div
        role="alert"
        data-testid="import-blocked"
        className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800"
      >
        {importBlockedMessage(usage.block, session.usesHour12)}
      </div>
    );
  }

  const profileId = selectedProfile.id === "contracts" ? "contracts" : "candidates";

  const send = async () => {
    try {
      await createRequest.mutateAsync({
        p_company_id: session.companyId,
        p_kind: usage.kind,
        p_rows: usage.requested ?? 0,
        p_note: note.trim() || null,
      });
      // The file stays in this browser so the import can continue once it is approved.
      const kept = await savePendingImport({ companyId: session.companyId, profileId, source });
      setImportData((prev) => (prev.usage ? { ...prev, usage: { ...prev.usage, pending_request: true } } : prev));
      setOpen(false);
      showToast({
        description: kept
          ? "Solicitud enviada. Tu archivo queda guardado en este navegador; cuando Orquesti responda te avisamos en las notificaciones y podrás continuar."
          : "Solicitud enviada. Cuando Orquesti responda te avisamos en las notificaciones; tendrás que volver a cargar el archivo.",
        variant: "success",
      });
    } catch {
      // The hook already shows the error.
    }
  };

  return (
    <div
      role="alert"
      data-testid="import-over-limit"
      className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800 space-y-3"
    >
      <p>{usageLimitMessage(usage)}</p>

      {usage.pending_request ? (
        <p data-testid="import-request-pending">
          Ya enviaste una solicitud a Orquesti. Te avisamos en las notificaciones cuando responda y podrás
          continuar esta importación desde este mismo navegador.
        </p>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="import-request-open">
          Pedir ampliación
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Pedir ampliación</DialogTitle>
            <DialogDescription>
              Orquesti revisa la solicitud. Solo se envía cuántas filas necesitas y tu nota: los datos de
              tu archivo se quedan en este navegador.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            label="¿Para qué necesitas importar más? (opcional)"
            value={note}
            maxLength={1000}
            onChange={(event) => setNote(event.target.value)}
            data-testid="import-request-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={createRequest.isPending}>
              Cancelar
            </Button>
            <Button onClick={send} disabled={createRequest.isPending} data-testid="import-request-send">
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
